import { Component, OnInit } from '@angular/core';
@Component({ selector: 'app-sol-fleet', templateUrl: './fleet-logistics.component.html', styleUrls: ['./fleet-logistics.component.scss'], standalone: false })
export class FleetLogisticsComponent implements OnInit {
  activeSection = 'overview';
  sections = [
    { id: 'overview', label: 'Overview', icon: 'bx-layer' },
    { id: 'problem', label: 'Problem', icon: 'bx-error-circle' },
    { id: 'features', label: 'Key Features', icon: 'bx-chip' },
    { id: 'usecases', label: 'Use Cases', icon: 'bx-briefcase' },
    { id: 'all-features', label: 'All Features', icon: 'bx-grid-alt' },
  ];
  overviewHighlights = [
    { val: 'Device + Mobile', label: 'Flexible telematics deployment' },
    { val: 'ERP / SAP Ready', label: 'Integrated transport billing workflows' },
    { val: 'FleetIQ', label: 'Unified fleet operations platform' },
  ];

  keyFeatures = [
    {
      icon: 'bx-devices',
      title: 'Device & Mobile Telematics',
      desc: 'Track and manage fleets using telematics devices or driver mobile apps for owned, hired, and shared vehicles.'
    },
    {
      icon: 'bx-pie-chart-alt-2',
      title: 'Fleet Utilization Status',
      desc: 'Get real-time visibility into active, idle, and underutilized vehicles to maximize utilization and reduce operational costs.'
    },
    {
      icon: 'bx-file',
      title: 'Scheduled Reports',
      desc: 'Automatically generate and deliver fleet, trip, and performance reports at predefined intervals without manual effort.'
    },
    {
      icon: 'bx-time-five',
      title: 'Document Expiry Reminders',
      desc: 'Stay compliant with automated alerts and reports for vehicle, driver, and permit document expiries.'
    },
    {
      icon: 'bx-trip',
      title: 'Trip Management Module',
      desc: 'Plan, assign, monitor, and close trips with complete end-to-end visibility and operational control.'
    },
    {
      icon: 'bx-map-alt',
      title: 'Route Optimization',
      desc: 'Optimize routes based on distance, traffic, and operational constraints to improve efficiency and lower fuel expenses.'
    },
    {
      icon: 'bx-link-alt',
      title: 'Enterprise Integrations',
      desc: 'Integrate fleet and trip data with SAP, ERP, and Salesforce to enable billing accuracy and streamlined workflows.'
    },
    {
      icon: 'bx-lock-alt',
      title: 'Role-Based Access Control',
      desc: 'Control data visibility and system access by assigning permissions based on user roles and responsibilities.'
    },
  ];

  useCases = [
    {
      title: 'Transport Billing System',
      icon: 'bx-receipt',
      desc: 'Uses GPS and geofence-based trip validation to automate transparent billing, reduce disputes, and prevent revenue leakage with audit-ready movement data.'
    },
    {
      title: 'ERP / SAP Integrated Trip Billing',
      icon: 'bx-transfer-alt',
      desc: 'Syncs trip execution data with ERP parameters, completion status, utilization, and rate cards to reduce reconciliation effort and improve governance.'
    },
    {
      title: 'Driver vs Vehicle Tracking',
      icon: 'bx-map-pin',
      desc: 'Combines mobile and device-based telemetry to map driver and vehicle association, detect mismatches, and improve accountability.'
    },
    {
      title: 'Field Force Management',
      icon: 'bx-user-pin',
      desc: 'Enables deviceless field-force visibility, territory coverage monitoring, and productivity tracking without additional hardware.'
    },
    {
      title: 'Last Mile Delivery Management',
      icon: 'bx-package',
      desc: 'Supports route optimization, proof of delivery, delay alerts, expense tracking, and delivery performance analytics.'
    },
    {
      title: 'Corporate Mobility & Employee Safety',
      icon: 'bx-shield-quarter',
      desc: 'Delivers live trip visibility, geofencing alerts, route deviation detection, and automated reports for duty-of-care compliance.'
    },
    {
      title: 'Driver Rewards & Incentives',
      icon: 'bx-gift',
      desc: 'Links rewards to distance traveled, driving behavior, and trip performance to improve compliance and retention.'
    },
    {
      title: 'Employee / Passenger Transport Attendance',
      icon: 'bx-id-card',
      desc: 'Automates attendance using verified trip and location data with audit-ready logs, safety events, and compliance reports.'
    },
  ];

  featureGroups = [
    {
      title: 'Operations & Visibility',
      icon: 'bx-radar',
      items: [
        { name: 'Dashboard', desc: 'Total number of vehicles along with their current status.' },
        // { name: 'Search', desc: 'Search any vehicle in the fleet and view its current location on the map.' },
        // { name: 'Active', desc: 'Shows vehicles currently being driven along with details of each vehicle.' },
        // { name: 'Parked', desc: 'Shows vehicles currently parked along with related vehicle details.' },
        // { name: 'Attention', desc: 'Highlights vehicles not sending data for 72 hours or more.' },
        { name: 'Trip Management Module', desc: 'Plan, assign, monitor, and close trips with end-to-end visibility.' },
        { name: 'Route Optimization', desc: 'Optimize trips using route, traffic, and operational constraints.' },
        // { name: 'Last Seen', desc: 'Track the vehicle and get information about the last seen location.' },
        // { name: 'Track', desc: 'Check the real-time location of the vehicle.' },
        // { name: 'Travel Distance', desc: 'Distance travelled by the vehicle on a given date.' },
        // { name: 'Activity Tracker', desc: 'Track activity on a selected date including geofences and alerts.' },
        { name: 'Activity Replay', desc: 'Replay completed trips at 1X, 2X, and 4X speeds.' },
        { name: 'Trip Logs', desc: 'Break down activity into trips with time and location details.' },
        { name: 'Weekly Summary', desc: 'Present weekly vehicle usage details in hours.' },
      ],
    },
    {
      title: 'Alerts, Maintenance & Compliance',
      icon: 'bx-bell',
      items: [
        { name: 'Engine Errors', desc: 'Generate alerts for identified engine errors.' },
        { name: 'Speed Alert', desc: 'Alert generated regarding overspeeding.' },
        { name: 'Device Plug Out', desc: 'Alert generated if the device has been unplugged.' },
        { name: 'Ignition On / Off', desc: 'Alerts generated for ignition on and off events.' },
        { name: 'Rash Acceleration', desc: 'Alert generated in case of rash acceleration.' },
        { name: 'Out of Sync', desc: 'Alert generated if the vehicle is not sending data for 24 hours.' },
        // { name: 'Servicing', desc: 'Alerts generated regarding due servicing.' },
        { name: 'Geofencing', desc: 'Alerts for entry and exit to configured geofences.' },
        // { name: 'Error Cleared', desc: 'Alerts generated when identified errors are resolved.' },
        // { name: 'Idling', desc: 'Alert when the vehicle is stationary while ignition remains on.' },
        // { name: 'Hard Braking', desc: 'Alert generated in case of hard braking events.' },
        // { name: 'Document Expiry', desc: 'Alerts for license, insurance, PUC, and related document expiries.' },
        // { name: 'Battery', desc: 'Alert generated for low battery voltage.' },
        // { name: 'Documents', desc: 'Store vehicle documents digitally and notify before expiry dates.' },
        // { name: 'Service', desc: 'Add service details such as date and type of servicing.' },
        { name: 'Settings', desc: 'Configure speed limit, alert intervals, out-of-sync, geofencing, and related controls.' },
      ],
    },
    {
      title: 'Reporting, Billing & Analytics',
      icon: 'bx-spreadsheet',
      items: [
        { name: 'Reports', desc: 'Download consolidated groupwise reports.' },
        { name: 'Advanced Report', desc: 'Download detailed vehiclewise reports.' },
        // { name: 'Schedule Report', desc: 'Schedule daily, weekly, and monthly reports for selected recipients.' },
        // { name: 'Alert Report', desc: 'Generate consolidated alert reports for single or multiple alert types.' },
        // { name: 'Attention Report', desc: 'Shows date and location when the vehicle went out of sync.' },
        // { name: 'Daily Activity Report', desc: 'Shows critical daily vehicle data for selected date ranges.' },
        // { name: 'Daily Activity With Trip Report', desc: 'Provides trip-level details for each day in the selected period.' },
        // { name: 'Details Report', desc: 'Shows vehicle details, last seen location, and parking status.' },
        // { name: 'Error Report', desc: 'Lists vehicles with issues, descriptions, and possible solutions.' },
        // { name: 'Expense Report', desc: 'Calculates fuel, toll, parking fees, and other trip expenses.' },
        // { name: 'Geofence Details Report', desc: 'Shows entry and exit details with date and time for associated geofences.' },
        // { name: 'Holding Time Report', desc: 'Checks parking time at configured start and end stations.' },
        // { name: 'Passenger Transport Reports', desc: 'Includes anomaly, scan, and summary reports for employee transport attendance systems.' },
        // { name: 'Pull Out Summary', desc: 'Lists all vehicles whose FleetIQ devices have been plugged out with time and location.' },
        // { name: 'Safety Index Report', desc: 'Shows a vehicle safety score based on weighted driving parameters.' },
        // { name: 'Station Trip / TTS Reports', desc: 'Provide trip details between stations or between two geofences.' },
        // { name: 'Transporter Billing Reports', desc: 'Generate billing and expense reports for completed and incomplete trips, including SAP-based transport billing.' },
        // { name: 'Trip Details / Summary Reports', desc: 'Provide detailed and summarized views of trip and vehicle usage.' },
        // { name: 'Vehicle Document Expiry Report', desc: 'Track document expiry across the fleet.' },
        // { name: 'Vessel Daily Activity Report', desc: 'Covers distance, average speed, and top speed for vessels.' },
        // { name: 'Audit / Fuel Pilferage / IoT / PID / Temperature Reports', desc: 'Advanced reporting for audits, fuel pilferage, raw device data, PID location-speed data, and cold-storage temperature grading.' },
        // { name: 'Analytics', desc: 'Weekly analytics showing driver score and gear percentage trends.' },
      ],
    },
    {
      title: 'Admin, Assets, Drivers & Support',
      icon: 'bx-cog',
      items: [
        // { name: 'Company Profile', desc: 'Manage company details along with PAN and GST number.' },
        // { name: 'User Profile', desc: 'Manage superadmin details such as mobile number and email.' },
        { name: 'Parking Hub', desc: 'Bulk upload parking hub locations.' },
        { name: 'Places', desc: 'Create and update geofence details.' },
        // { name: 'Theme Setting', desc: 'Change the dashboard color theme.' },
        // { name: 'Add / Remove Vehicle', desc: 'Single and bulk upload options to onboard vehicles.' },
        // { name: 'View Vehicles', desc: 'List vehicles in grid or list view.' },
        // { name: 'Vehicle Details', desc: 'View and edit fuel type, added date, manufacturing year, and related details.' },
        // { name: 'Driver Details', desc: 'Edit driver details associated with each vehicle.' },
        // { name: 'Driver ID', desc: 'Track the serial number of the device associated with vehicles.' },
        // { name: 'Transactions', desc: 'Record and show all transactions on selected vehicles.' },
        // { name: 'Actions', desc: 'Run supported actions from the vehicle detail screen.' },
        // { name: 'Create / Remove Group', desc: 'Manage logical groups of vehicles based on categories.' },
        // { name: 'Associate Asset', desc: 'Add or delete vehicles in a group.' },
        // { name: 'Associate Operator', desc: 'Add or delete personnel at the group level.' },
        // { name: 'Group Settings', desc: 'Set geofence and speed limits for all vehicles in a group.' },
        // { name: 'Add Driver', desc: 'View, add, and remove drivers from the fleet.' },
        // { name: 'Driver Information', desc: 'Store mobile number, joining date, license number, and expiry date.' },
        // { name: 'Associated Vehicle', desc: 'Map the driven vehicle number and vehicle type to each driver.' },
        { name: 'Weekly Activity & Driving Summary', desc: 'Analyze weekly travel duration, day/night travel, kilometres covered, and infractions.' },
        // { name: 'Add / Remove Operator', desc: 'Create operator access for group heads.' },
        // { name: 'Operator Settings', desc: 'Assign operators to groups and provide login access.' },
        // { name: 'Help Guide', desc: 'Built-in guidance to understand system features.' },
        // { name: 'Ask an Expert', desc: 'Ask vehicle-related queries directly to automotive experts.' },
        // { name: 'Report Bugs / Suggest Features', desc: 'Capture platform bugs and feature requests from users.' },
      ],
    },
  ];

  ngOnInit() {}

  scrollTo(id: string) {
    this.activeSection = id;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
